import { useEffect, useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '../context/ProfileContext';
import { useAccessRolesQuery } from '../../application/profileQueries';
import { ProfilePickerPage } from '../pages/ProfilePickerPage';
import { RemoteDeviceRepository } from '@/modules/devices/infrastructure/repositories/RemoteDeviceRepository';
import { LicenseExceededError, LicenseNotFoundError } from '@/modules/devices/domain/errors/DeviceErrors';
import { useDeviceLicenseState } from '@/modules/devices/presentation/context/DeviceLicenseContext';

/**
 * ProfileGate — route wrapper for `/select-profile`.
 *
 * Spec: profile-auto-selection R1..R5
 *
 * Behavior:
 *   - When `useAccessRolesQuery` resolves with exactly one AccessRole AND
 *     `selectedRole === null`, calls `selectRole(roles[0])` then
 *     `navigate('/', { replace: true })`. The picker UI is NEVER mounted in
 *     this path (avoids flash of picker before navigation).
 *   - When `roles.length === 0` or `roles.length >= 2`, renders the existing
 *     `ProfilePickerPage` (empty state / list / etc).
 *   - When `selectedRole !== null` (deep-link / re-entry / future "switch
 *     profile" UX), renders the picker without auto-selecting.
 *
 * Pitfalls handled:
 *   - StrictMode double-fire: `decidedRef` blocks re-entry of the effect after
 *     the auto-select fires once.
 *   - Back-button trap: `replace: true` swaps the history entry so the user
 *     pops to `/login`, not back into the gate (which would re-auto-select
 *     and loop).
 */
export function ProfileGate(): ReactElement {
  const navigate = useNavigate();
  const { selectedRole, selectRole } = useProfile();
  const { data: roles, isLoading, isSuccess } = useAccessRolesQuery();
  const { T } = useTheme();
  const decidedRef = useRef(false);
  const { setLicenseError } = useDeviceLicenseState();
  const licenseCheckedRef = useRef(false);

  const shouldAutoSelect =
    isSuccess && selectedRole === null && roles !== undefined && roles.length === 1;

  useEffect(() => {
    if (decidedRef.current) return;
    if (!shouldAutoSelect) return;
    // Roles is guaranteed non-undefined and length === 1 here (shouldAutoSelect).
    const onlyRole = roles![0]!;
    decidedRef.current = true;
    selectRole(onlyRole);
    void navigate('/', { replace: true });
  }, [shouldAutoSelect, roles, selectRole, navigate]);

  // Re-checks the device license against the backend on every entry to this
  // screen. Fail-closed: ANY error (business or network/timeout/server) blocks.
  useEffect(() => {
    if (licenseCheckedRef.current) return;
    licenseCheckedRef.current = true;

    const idDeviceRaw = localStorage.getItem('mi_enrolled_device_id');
    if (!idDeviceRaw) return;

    const idDevice = Number(idDeviceRaw);
    if (!Number.isFinite(idDevice) || idDevice <= 0) return;

    void new RemoteDeviceRepository()
      .findById(idDevice)
      .then(() => {
        setLicenseError(null);
      })
      .catch((error: unknown) => {
        if (error instanceof LicenseExceededError) {
          setLicenseError('exceeded');
        } else if (error instanceof LicenseNotFoundError) {
          setLicenseError('not-found');
        } else {
          setLicenseError('unknown');
        }
      });
  }, [setLicenseError]);

  // While the query is loading OR while the auto-select decision is in flight,
  // render a thin loading state instead of the picker — prevents flash of
  // picker UI on the 1-role path.
  if (isLoading || shouldAutoSelect) {
    return (
      <div
        data-testid="profile-gate-loading"
        aria-busy="true"
        style={{
          width: '100vw',
          height: '100vh',
          background: T.bg,
        }}
      />
    );
  }

  return <ProfilePickerPage />;
}
